<script setup lang="ts">
import {
  Switch as HeadlessSwitch,
  SwitchGroup as HeadlessSwitchGroup,
  SwitchLabel as HeadlessSwitchLabel,
} from '@headlessui/vue'

import type { SwitchProps } from './types'

const model = defineModel<boolean>({ default: false })

withDefaults(defineProps<SwitchProps>(), {
  size: 'md',
  disabled: false,
  hideLabel: false,
})

const emit = defineEmits<{ change: [checked: boolean] }>()

function update(checked: boolean): void {
  model.value = checked
  emit('change', checked)
}
</script>

<template>
  <HeadlessSwitchGroup as="template">
    <div
      class="ui-switch inline-flex items-center gap-xs font-sans text-fg"
      :class="[`ui-switch--${size}`, { 'ui-switch--disabled opacity-60': disabled }]"
    >
      <HeadlessSwitch
        :model-value="model"
        :disabled="disabled"
        class="ui-switch__control relative box-border inline-flex flex-none cursor-pointer items-center rounded-full border-0 bg-border-strong p-0.5 outline-none transition-colors duration-150 focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]"
        :class="[
          {
            'is-checked bg-primary': model,
            'cursor-not-allowed': disabled,
          },
          size === 'xs' || size === 'sm' ? 'h-[1.125rem] w-8' : '',
          size === 'md' ? 'h-5 w-10' : '',
          size === 'lg' || size === 'xl' ? 'h-[1.625rem] w-12' : '',
        ]"
        :aria-disabled="disabled"
        :aria-label="label"
        @update:model-value="update"
      >
        <span
          class="ui-switch__thumb block rounded-full bg-surface shadow-pop transition-transform duration-150"
          :class="[
            size === 'xs' || size === 'sm' ? 'h-3 w-3' : '',
            size === 'md' ? 'h-4 w-4' : '',
            size === 'lg' || size === 'xl' ? 'h-5 w-5' : '',
            model && (size === 'xs' || size === 'sm') ? 'translate-x-[0.875rem]' : '',
            model && size === 'md' ? 'translate-x-5' : '',
            model && (size === 'lg' || size === 'xl') ? 'translate-x-[1.375rem]' : '',
          ]"
          aria-hidden="true"
        />
      </HeadlessSwitch>
      <HeadlessSwitchLabel
        :class="
          hideLabel
            ? 'ui-switch__label--sr-only sr-only'
            : [
                'ui-switch__label cursor-pointer text-[0.8125rem] leading-tight',
                { 'cursor-not-allowed': disabled },
              ]
        "
      >
        {{ label }}
      </HeadlessSwitchLabel>
    </div>
  </HeadlessSwitchGroup>
</template>
